import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { WAREHOUSES_LIST } from '../lib/constants';
import {
  STORAGE_CACHE_KEY,
  SAMPLE_INITIAL_REPORTS,
  isSentinelSeverity
} from '../lib/riskConstants';

export function useDentalRiskReports(user = null, onNavigate = null) {
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCase, setSelectedCase] = useState(null);
  const [isSavingInvestigation, setIsSavingInvestigation] = useState(false);

  // Filters State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDomain, setFilterDomain] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSeverityGroup, setFilterSeverityGroup] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');

  // Load reports from Supabase with LocalStorage & Sample fallback
  const loadReports = useCallback(async () => {
    setIsLoading(true);
    let loadedFromDb = false;

    if (supabase) {
      try {
        const { data, error } = await supabase
          .from('dental_risk_reports')
          .select('*')
          .order('created_at', { ascending: false });

        if (!error && data) {
          setReports(data);
          localStorage.setItem(STORAGE_CACHE_KEY, JSON.stringify(data));
          loadedFromDb = true;
        } else {
          console.warn('Supabase fetch returned error (Table may not exist yet):', error?.message);
        }
      } catch (err) {
        console.warn('Supabase fetch exception:', err);
      }
    }

    if (!loadedFromDb) {
      const cached = localStorage.getItem(STORAGE_CACHE_KEY);
      if (cached) {
        try {
          setReports(JSON.parse(cached));
        } catch (e) {
          setReports(SAMPLE_INITIAL_REPORTS);
        }
      } else {
        setReports(SAMPLE_INITIAL_REPORTS);
        localStorage.setItem(STORAGE_CACHE_KEY, JSON.stringify(SAMPLE_INITIAL_REPORTS));
      }
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  // Save new report from Wizard
  const handleSaveNewReport = async (newReportData) => {
    const d = new Date();
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const count = reports.length + 1;
    const incidentNumber = `D-RSK-${yy}${mm}-${String(count).padStart(3, '0')}`;

    const recordToInsert = {
      ...newReportData,
      incident_number: incidentNumber,
      status: 'pending',
      created_at: new Date().toISOString(),
      last_updated: new Date().toISOString()
    };

    let insertedSuccessfully = false;

    if (supabase) {
      try {
        const { data, error } = await supabase
          .from('dental_risk_reports')
          .insert([recordToInsert])
          .select();

        if (!error && data && data.length > 0) {
          const inserted = data[0];
          const updated = [inserted, ...reports];
          setReports(updated);
          localStorage.setItem(STORAGE_CACHE_KEY, JSON.stringify(updated));
          insertedSuccessfully = true;
          return inserted;
        } else {
          console.warn('Supabase insert fallback:', error?.message);
        }
      } catch (err) {
        console.warn('Supabase insert exception:', err);
      }
    }

    if (!insertedSuccessfully) {
      const fallbackRecord = {
        ...recordToInsert,
        id: 'local-' + Date.now()
      };
      const updated = [fallbackRecord, ...reports];
      setReports(updated);
      localStorage.setItem(STORAGE_CACHE_KEY, JSON.stringify(updated));
      return fallbackRecord;
    }
  };

  // Update RCA & Investigation (CAPA)
  const handleUpdateInvestigation = async (targetCase, investigationData) => {
    const caseToUpdate = targetCase || selectedCase;
    if (!caseToUpdate) return;
    setIsSavingInvestigation(true);

    const isNowClosed = investigationData.status === 'closed' && caseToUpdate.status !== 'closed';
    const patch = {
      status: investigationData.status,
      investigator_name: investigationData.investigator_name,
      root_cause_analysis: investigationData.root_cause_analysis,
      rca_fishbone_category: investigationData.rca_fishbone_category || caseToUpdate.rca_fishbone_category || null,
      corrective_action: investigationData.corrective_action,
      closed_at: isNowClosed ? new Date().toISOString() : caseToUpdate.closed_at,
      last_updated: new Date().toISOString()
    };

    let dbUpdated = false;

    if (supabase && !String(caseToUpdate.id).startsWith('local-') && !String(caseToUpdate.id).startsWith('sample-')) {
      try {
        const { data, error } = await supabase
          .from('dental_risk_reports')
          .update(patch)
          .eq('id', caseToUpdate.id)
          .select();

        if (!error && data && data.length > 0) {
          dbUpdated = true;
          const updatedRecord = data[0];
          const updatedList = reports.map((r) => (r.id === caseToUpdate.id ? updatedRecord : r));
          setReports(updatedList);
          setSelectedCase(updatedRecord);
          localStorage.setItem(STORAGE_CACHE_KEY, JSON.stringify(updatedList));
        }
      } catch (err) {
        console.warn('Update investigation exception:', err);
      }
    }

    if (!dbUpdated) {
      const updatedRecord = { ...caseToUpdate, ...patch };
      const updatedList = reports.map((r) => (r.id === caseToUpdate.id ? updatedRecord : r));
      setReports(updatedList);
      setSelectedCase(updatedRecord);
      localStorage.setItem(STORAGE_CACHE_KEY, JSON.stringify(updatedList));
    }

    setIsSavingInvestigation(false);
  };

  // Quick Move status (e.g. from Kanban column)
  const handleQuickStatusMove = async (reportId, nextStatus) => {
    const target = reports.find((r) => r.id === reportId);
    if (!target) return;

    const patch = {
      status: nextStatus,
      closed_at: nextStatus === 'closed' ? new Date().toISOString() : target.closed_at,
      last_updated: new Date().toISOString()
    };

    const updatedList = reports.map((r) => (r.id === reportId ? { ...r, ...patch } : r));
    setReports(updatedList);
    if (selectedCase && selectedCase.id === reportId) {
      setSelectedCase((prev) => ({ ...prev, ...patch }));
    }
    localStorage.setItem(STORAGE_CACHE_KEY, JSON.stringify(updatedList));

    if (supabase && !String(reportId).startsWith('local-') && !String(reportId).startsWith('sample-')) {
      try {
        await supabase.from('dental_risk_reports').update(patch).eq('id', reportId);
      } catch (e) {
        // silent fallback
      }
    }
  };

  // Trigger Recall Batch for CSSD sterilization failure or critical events
  const handleTriggerRecall = async (theCase) => {
    if (!theCase) return;
    try {
      if (supabase && theCase.id && !String(theCase.id).startsWith('local-') && !String(theCase.id).startsWith('sample-')) {
        await supabase
          .from('dental_risk_reports')
          .update({ recall_batch_triggered: true, last_updated: new Date().toISOString() })
          .eq('id', theCase.id);
      }
      const updatedList = reports.map((r) =>
        r.incident_number === theCase.incident_number ? { ...r, recall_batch_triggered: true } : r
      );
      setReports(updatedList);
      setSelectedCase((prev) => (prev ? { ...prev, recall_batch_triggered: true } : null));
      localStorage.setItem(STORAGE_CACHE_KEY, JSON.stringify(updatedList));
    } catch (e) {
      console.warn('Error triggering recall in report:', e);
    }

    if (typeof onNavigate === 'function') {
      onNavigate('recall', {
        barcode: theCase.instrument_barcode,
        cycle: theCase.sterile_cycle_id
      });
    } else {
      window.dispatchEvent(
        new CustomEvent('dental-risk-navigate-recall', {
          detail: {
            barcode: theCase.instrument_barcode,
            cycle: theCase.sterile_cycle_id
          }
        })
      );
      alert(`🚨 บันทึกคำสั่งเปิดเคส Recall สำหรับ ${theCase.incident_number} เรียบร้อยแล้ว!\nสามารถตรวจสอบรายละเอียดในหน้า 'เรียกคืนวัสดุ (Recall)'`);
    }
  };

  // Export to CSV with UTF-8 BOM
  const handleExportCSV = () => {
    if (reports.length === 0) {
      alert('ไม่มีข้อมูลสำหรับส่งออก');
      return;
    }
    const headers = [
      'Incident Number',
      'Date',
      'Location',
      'Domain',
      'Severity Level',
      'Title',
      'Category',
      'Sub Category',
      'Patient HN',
      'Status',
      'Reporter',
      'Investigator',
      'RCA',
      'CAPA'
    ];
    const rows = reports.map((r) => [
      `"${r.incident_number || ''}"`,
      `"${r.incident_date ? new Date(r.incident_date).toLocaleString('th-TH') : ''}"`,
      `"${r.location || ''}"`,
      `"${r.risk_domain || ''}"`,
      `"${r.severity_level || ''}"`,
      `"${(r.title || '').replace(/"/g, '""')}"`,
      `"${(r.category || '').replace(/"/g, '""')}"`,
      `"${(r.sub_category || '').replace(/"/g, '""')}"`,
      `"${r.patient_hn || ''}"`,
      `"${r.status || ''}"`,
      `"${r.is_anonymous ? 'นิรนาม' : r.reporter_name || ''}"`,
      `"${r.investigator_name || ''}"`,
      `"${(r.root_cause_analysis || '').replace(/"/g, '""')}"`,
      `"${(r.corrective_action || '').replace(/"/g, '""')}"`
    ]);

    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `dental_risk_reports_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setFilterDomain('all');
    setFilterStatus('all');
    setFilterSeverityGroup('all');
    setFilterLocation('all');
  };

  // Metrics computation
  const metrics = useMemo(() => {
    const total = reports.length;
    const pending = reports.filter((r) => r.status === 'pending').length;
    const investigating = reports.filter((r) => r.status === 'investigating').length;
    const resolved = reports.filter((r) => r.status === 'resolved').length;
    const closed = reports.filter((r) => r.status === 'closed').length;
    const resolvedOrClosed = resolved + closed;

    const sentinel = reports.filter((r) => isSentinelSeverity(r.severity_level)).length;

    return { total, pending, investigating, resolved, closed, resolvedOrClosed, sentinel };
  }, [reports]);

  // Unique locations from warehouses and reports
  const uniqueLocations = useMemo(() => {
    const set = new Set();
    WAREHOUSES_LIST.forEach((w) => {
      const name = typeof w === 'string' ? w : w.name.trim().replace(/^-\s*/, '');
      if (name) set.add(name);
    });
    reports.forEach((r) => {
      if (r.location) set.add(r.location);
    });
    return Array.from(set);
  }, [reports]);

  // Filtered reports
  const filteredReports = useMemo(() => {
    return reports.filter((r) => {
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchTitle = (r.title || '').toLowerCase().includes(q);
        const matchDesc = (r.description || '').toLowerCase().includes(q);
        const matchNum = (r.incident_number || '').toLowerCase().includes(q);
        const matchHN = (r.patient_hn || '').toLowerCase().includes(q);
        const matchLoc = (r.location || '').toLowerCase().includes(q);
        const matchCat = (r.category || '').toLowerCase().includes(q);
        const matchReporter = (r.reporter_name || '').toLowerCase().includes(q);
        if (!matchTitle && !matchDesc && !matchNum && !matchHN && !matchLoc && !matchCat && !matchReporter) {
          return false;
        }
      }

      if (filterDomain !== 'all' && r.risk_domain !== filterDomain) return false;
      if (filterStatus !== 'all' && r.status !== filterStatus) return false;
      if (filterLocation !== 'all' && r.location !== filterLocation) return false;

      if (filterSeverityGroup !== 'all') {
        const lvl = String(r.severity_level || '').toUpperCase();
        if (filterSeverityGroup === 'sentinel' && !['G', 'H', 'I', '4', '5'].includes(lvl)) return false;
        if (filterSeverityGroup === 'harm' && !['E', 'F', '3'].includes(lvl)) return false;
        if (filterSeverityGroup === 'near_miss' && !['A', 'B', 'C', 'D', '1', '2'].includes(lvl)) return false;
      }

      return true;
    });
  }, [reports, searchQuery, filterDomain, filterStatus, filterLocation, filterSeverityGroup]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (searchQuery.trim()) count++;
    if (filterDomain !== 'all') count++;
    if (filterStatus !== 'all') count++;
    if (filterSeverityGroup !== 'all') count++;
    if (filterLocation !== 'all') count++;
    return count;
  }, [searchQuery, filterDomain, filterStatus, filterSeverityGroup, filterLocation]);

  return {
    reports,
    filteredReports,
    isLoading,
    metrics,
    uniqueLocations,
    selectedCase,
    setSelectedCase,
    isSavingInvestigation,
    // Filters
    searchQuery,
    setSearchQuery,
    filterDomain,
    setFilterDomain,
    filterStatus,
    setFilterStatus,
    filterSeverityGroup,
    setFilterSeverityGroup,
    filterLocation,
    setFilterLocation,
    activeFiltersCount,
    clearFilters,
    // Actions
    loadReports,
    handleSaveNewReport,
    handleUpdateInvestigation,
    handleQuickStatusMove,
    handleTriggerRecall,
    handleExportCSV
  };
}
